import { Box, Button, createStyles, Group, Input, Popover, Skeleton, Stack, Text, useMantineTheme } from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconFileSymlink, IconPlus, IconSearch, IconTrash, IconX } from '@tabler/icons-react';
import { CheckboxDropdown, FilterButton } from 'core/components/index';
import { useCallback, useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { getSeasonMasters } from 'services/seasonService';
import { CompanyTableColumns } from 'utils/constants';
import { getCompany, matchesKeywords, selectItems } from 'utils/helper';
import { logger } from 'utils/logger';
import { SampleFilterModel } from 'core/models/SampleFilterModel';
import { useParams } from 'react-router-dom';
import SampleList from 'modules/Warehouse/features/samples/SampleList';
import moment from 'moment';
import SeasonMastersStyleFilterPanel from './SeasonMastersStyleFilterPanel';
import { EmptyState } from 'core/components/emptyState/EmptyState';
import { openConfirmModal } from '@mantine/modals';

const useStyles = createStyles((theme) => ({
    sampleListContainer: {
        '& .mantine-Table-root': {
            // Override table styles here
            fontSize: '12px',
        },
        '& .mantine-Table-th': {
            // Override header styles
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[1],
            fontWeight: 600,
        },
        '& .mantine-Table-td': {
            // Override cell styles
            padding: '8px 12px',
        },
        '.h-scroll-table-container table thead tr th': {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[1] + ' !important',
            fontWeight: 600,
        },
        '.pagination-footer': {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[1] + ' !important',
        },
        '.mantine-ScrollArea-root': {
            width: '100%',
            paddingRight: theme.spacing.md,
        },
        '.h-scroll-table thead th:nth-child(2), .h-scroll-table tbody td:nth-child(2)': {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[1] + ' !important',
        },
        // Add any other specific class overrides you need
    },
}));

const MastersStyleImport = ({ user, onAction, onCancel, aggData, season }) => {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const searchInputRef = useRef(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [keyword, setKeyword] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [filterOpen, setFilterOpen] = useState(false);
    const [filterItems, setFilterItems] = useState(null);
    const [filterCount, setFilterCount] = useState(0);
    // const [sampleFilters, setSampleFilters] = useState({});
    const [sampleFilter, setSampleFilter] = useState({
        ...new MastersStyleFilterModel(),
    });
    const params = useParams();
    const [sampleData, setSampleData] = useState([]);
    const company = getCompany(aggData);
    const keycode = CompanyTableColumns[company.code]?.seasonMasters[0]?.accessor;

    const filterSamples = useCallback(() => {
        logger.log('sampleFilter', sampleFilter);
        var data = sampleData?.filter((sample) => {
            var match = true;
            // filter by brand
            if (sampleFilter.brands && sampleFilter.brands.length) {
                match = sampleFilter.brands.indexOf(sample.facet_brand) > -1 ? true : false;
            }

            if (sampleFilter.categories && sampleFilter.categories.length && match) {
                match = sampleFilter.categories.indexOf(sample.metadata_toplevelcategory) > -1 ? true : false;
            }

            if (sampleFilter.apns && sampleFilter.apns.length && match) {
                match = sampleFilter.apns.indexOf(sample.id) > -1 ? true : false;
            }

            logger.log('sample', sample.COLOUR);
            if (sampleFilter.colours && sampleFilter.colours.length && match) {
                match = sampleFilter.colours.indexOf(sample.COLOUR) > -1 ? true : false;
            }

            if (sampleFilter.variations && sampleFilter.variations.length && match) {
                match = sampleFilter.variations.indexOf(sample.VARIATION) > -1 ? true : false;
            }

            if (sampleFilter.variationSizes && sampleFilter.variationSizes.length && match) {
                match = sampleFilter.variationSizes.indexOf(sample['VARIATION SIZE']) > -1 ? true : false;
            }

            if (sampleFilter.productStatus && sampleFilter.productStatus.length && match) {
                match = sampleFilter.productStatus.indexOf(sample['PRODUCT STATUS']) > -1 ? true : false;
            }

            if (sampleFilter.styleNumbers && sampleFilter.styleNumbers.length && match) {
                match = sampleFilter.styleNumbers.indexOf(sample['STYLE NUMBER']) > -1 ? true : false;
            }

            if (sampleFilter.departments && sampleFilter.departments.length && match) {
                match = sampleFilter.departments.indexOf(sample.DEPT) > -1 ? true : false;
            }

            if (sampleFilter.photoCodes && sampleFilter.photoCodes.length && match) {
                match = sampleFilter.photoCodes.indexOf(sample[keycode]) > -1 ? true : false;
            }

            // Helper function to convert column accessor to filter field name (matches SeasonMastersStyleFilterPanel)
            const getFilterFieldName = (accessor) => {
                if (!accessor) return null;
                return accessor
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, ' ')
                    .trim()
                    .replace(/\s+(\w)/g, (_, c) => c.toUpperCase())
                    .replace(/^./, (c) => c.toLowerCase());
            };

            // Helper function to check if a column should be filterable
            const isFilterableColumn = (column) => {
                if (!column.accessor || column.accessor === 'expander' || column.accessor === 'action') {
                    return false;
                }
                // Date columns are handled separately
                if (column.Header && (column.Header.toLowerCase().includes('date') || column.Header.toLowerCase().includes('otb'))) {
                    return false;
                }
                return true;
            };

            // Add filters for the columns as per the SeasonMastersStyleFilterPanel
            if (match && columns) {
                for (const column of columns) {
                    if (!match) break; // Early exit if already filtered out
                    if (!isFilterableColumn(column)) continue;

                    const accessor = column.accessor;
                    const filterFieldName = getFilterFieldName(accessor);
                    const filterValue = sampleFilter[filterFieldName];

                    // Only filter if filter is set and has values
                    if (filterValue && Array.isArray(filterValue) && filterValue.length > 0) {
                        const sampleValue = sample[accessor];
                        if (sampleValue !== undefined && sampleValue !== null && sampleValue !== '') {
                            match = filterValue.indexOf(sampleValue) > -1;
                            if (!match) break; // Early exit if no match
                        } else {
                            // If sample value is empty but filter requires values, exclude it
                            match = false;
                            break;
                        }
                    }
                }
            }

            const otbDate = new Date(sample['OTB']);

            // Deadline filter dates
            if (sampleFilter.otbDate.dateStart && sampleFilter.otbDate.dateEnd && match) {
                match = otbDate >= moment(sampleFilter.otbDate.dateStart).toDate() && otbDate <= moment(sampleFilter.otbDate.dateEnd).toDate() ? true : false;
            } else if (sampleFilter.otbDate.dateStart && sampleFilter.otbDate.dateEnd === '' && match) {
                match = otbDate >= moment(sampleFilter.otbDate.dateStart).toDate() ? true : false;
            } else if (sampleFilter.otbDate.dateEnd && sampleFilter.otbDate.dateStart === '' && match) {
                match = otbDate <= moment(sampleFilter.otbDate.dateEnd).toDate() ? true : false;
            }

            if (sample && sampleFilter.keyword && sampleFilter.keyword.length && match) {
                match = matchesKeywords(
                    sampleFilter.keyword,
                    sample.instructions?.['Web Photography Code'],
                    sample.seasonSort,
                    sample.instructions?.['Web Photography Notes'],
                    sample.instructions?.['ODBMS APN (GTIN)'],
                    sample.instructions?.department,
                    sample.instructions?.Name,
                );
            }

            return match;
        });

        if (sampleFilter.groupBy) {
            var uniq = [...new Set(Object.keys(data).map((e) => data[e][sampleFilter.groupBy]))];
            // create the group by items
            var groupedData = uniq.map((group) => {
                var subRows = data.filter((val) => val[sampleFilter.groupBy] === group);
                return { type: 'group', name: group, subRows, id: group };
            });
            data = groupedData;
        }

        if (data) setFilteredData(data);
    }, [
        sampleFilter.brands,
        sampleFilter.categories,
        setFilteredData,
        sampleData,
        sampleFilter.colours,
        sampleFilter.keyword,
        sampleFilter.departments,
        sampleFilter.variations,
        sampleFilter.variationSizes,
        sampleFilter.productStatus,
        sampleFilter.stylesCodes,
        sampleFilter.productStatus,
    ]);

    const defaultColumns = [
        {
            // Build our expander column
            id: 'expander', // Make sure it has an ID
            Header: ({ getToggleAllRowsExpandedProps, isAllRowsExpanded }) => (
                <>
                    {/* <span {...getToggleAllRowsExpandedProps()}>
    {isAllRowsExpanded ? <IconChevronDown /> : <IconChevronRight />}
    </span> */}
                </>
            ),
            Cell: ({ row }) =>
                // Use the row.canExpand and row.getToggleRowExpandedProps prop getter
                // to build the toggle for expanding a row
                row.canExpand ? (
                    <span
                        {...row.getToggleRowExpandedProps({
                            style: {
                                // We can even use the row.depth property
                                // and paddingLeft to indicate the depth
                                // of the row
                                paddingLeft: `${row.depth * 2}rem`,
                            },
                        })}>
                        {row.isExpanded ? <IconChevronDown /> : <IconChevronRight />}
                    </span>
                ) : null,
        },
        // {
        //     accessor: 'targetSort',
        //     Header: 'APN',
        //     width: 200,
        // },
        // {
        //     accessor: 'keycode',
        //     Header: 'Keycode',
        //     width: 150,
        // },
        // {
        //     accessor: 'STYLE NUMBER',
        //     Header: 'Style Number',
        // },
        // {
        //     accessor: 'PRODUCT NAME',
        //     Header: 'Product Name',
        //     width: 300,
        // },
        // {
        //     accessor: 'DEPT',
        //     Header: 'Department',
        //     width: 200,
        // },
        // // {
        // //     accessor: 'BRAND',
        // //     Header: 'Brand',
        // // },
        // {
        //     accessor: 'COLOUR',
        //     Header: 'Colour',
        //     width: 120,
        // },
        // {
        //     accessor: 'VARIATION',
        //     Header: 'Variation',
        //     width: 100,
        // },
        // {
        //     accessor: 'VARIATION SIZE',
        //     Header: 'Variation Size',
        //     width: 120,
        // },
        // {
        //     accessor: 'PRODUCT STATUS',
        //     Header: 'Product Status',
        // },
        // {
        //     accessor: 'OTB',
        //     Header: 'OTB Date',
        //     width: 100,
        // },
    ];

    const [columns, setColumns] = useState(defaultColumns);

    useEffect(() => {
        //setSampleData(samples);

        // Dynamically build filter items based on columns
        const filterItemsMap = {};

        // Get company-specific columns (excluding default columns like expander)
        const companyColumns = columns.filter(
            (col) => col.accessor && col.accessor !== 'expander' && col.accessor !== 'action' && CompanyTableColumns[company?.code]?.seasonMasters?.some((c) => c.accessor === col.accessor),
        );

        // Build filter items for each column
        sampleData?.forEach((item) => {
            companyColumns.forEach((column) => {
                const accessor = column.accessor;
                const value = item[accessor];

                if (value !== undefined && value !== null && value !== '') {
                    // Use accessor as key, or convert to camelCase for consistency
                    const key = accessor
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, ' ')
                        .trim()
                        .replace(/\s+(\w)/g, (_, c) => c.toUpperCase())
                        .replace(/^./, (c) => c.toLowerCase());

                    if (!filterItemsMap[key]) {
                        filterItemsMap[key] = {};
                    }
                    filterItemsMap[key][value] = item;
                }
            });
        });

        setFilterItems(filterItemsMap);

        filterSamples();
    }, [sampleData, filterSamples, columns, company]);

    const setFilterVars = (vals) => {
        setSampleFilter({ ...vals, pageIndex: 0 });
    };

    const getFilterCount = (val) => {
        var count = 0;
        Object.entries(val).forEach(([key, value]) => {
            if (value?.length) {
                ++count;
            }
            if (key === 'status') {
                var statusCount = 0;
                Object.entries(value).forEach(([key, value]) => {
                    if (value && value.length) {
                        ++statusCount;
                    }
                });
                count += statusCount;
            }
            if (key === 'otbDate') {
                if (value.dateStart.toString() || value.dateEnd.toString()) {
                    ++count;
                }
            }
        });
        return count;
    };

    useEffect(() => {
        const count = getFilterCount(sampleFilter);
        setFilterCount(count);
    }, [sampleFilter]);

    const onClearClick = (e) => {
        e.stopPropagation();
        e.nativeEvent.stopPropagation();
        setSampleFilter({ ...new SampleFilterModel(), otbDate: { dateStart: '', dateEnd: '' } });
    };

    const filterButtonHandler = (e) => {
        setFilterOpen((o) => !o);
    };

    // Initialize component and set focus to search input
    useEffect(() => {
        // Set focus to search input on component mount
        if (searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current.focus();
            }, 100);
        }
    }, []);

    const selectAllMenu = (
        <div>
            <Button variant='default' onClick={() => onSelectAll()}>
                Deselect All
            </Button>
        </div>
    );

    const loaderRows = [1, 2, 3].map((val, key) => (
        <Box key={key} className='list-renderer-item' pt={'md'}>
            <Group position='apart'>
                <Group>
                    <Skeleton height={26} radius='xl' width={26} />
                    <div>
                        <Skeleton height={16} radius='xl' width={200} mb={'xs'} />
                        <Skeleton height={10} radius='xl' width={100} />
                    </div>
                </Group>
                <Skeleton height={32} radius='sm' width={85} />
            </Group>
        </Box>
    ));

    const onSelectAll = () => {
        // if there are items selected remove them
        if (selectedItems.length) {
            setSelectedItems([]);
            // dispatch(selectSamples([]));
        } else {
            setSelectedItems([...filteredData]);
            // dispatch(selectSamples([...sampleData]));
        }
    };

    const onItemSelected = (e, item, selected) => {
        var items = [...selectedItems];
        selectItems(e, item, selected, filteredData, items);
        setSelectedItems(items);
    };

    const onSubmitClick = () => {
        // show a confirm modal
        openConfirmModal({
            title: `Import ${selectedItems.length} Styles to the Event`,
            children: <Text size='sm'>Are you sure you want to import the selected styles to the event: {season.name}?</Text>,
            labels: {
                confirm: 'Yes, import Styles',
                cancel: 'Cancel',
            },
            onConfirm: () => {
                // grab the selected items and send them to the parent
                const payload = selectedItems.map((item) => {
                    var { id, index, instructions, keycode, targetSort, ...rest } = sampleData.find((val) => val['id'] === item.id);
                    return { ...rest };
                });

                // logger.log(payload, 'payload');
                onAction('import', payload);
            },
        });
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const val = await getSeasonMasters(keyword);

            const tmpData = val.map((item, idx) => ({
                index: idx,
                ...item,
                // keycode: item[keycode],
                id: idx.toString(),
                targetSort: item['APN'],
                instructions: {
                    'Web Photography Code': item['APN'],
                },
            }));
            setSampleData(tmpData);
        } catch (err) {
            logger.error(err);
            setSampleData([]);
        } finally {
            setIsLoading(false);
        }
    };

    const onItemClick = (item) => {
        // Handle item click
    };

    const onMenuItemClick = (e) => {
        // Handle menu item click
    };

    const onStatusClick = (e) => {
        // Handle status click
    };

    const rowMenuItems = [
        {
            handler: (data) => {
                // openSessionModal([data]);
            },
            label: 'Create Session',
            icon: <IconPlus size={14} />,
        },
        {
            handler: (data) => {
                // openAssignToSessionModal([data]);
            },
            label: 'Assign to Session',
            icon: <IconFileSymlink size={14} />,
        },
        {
            handler: (data) => {
                // onMenuItemClick({ e: SessionActions.REMOVE_SAMPLE, data: [data] });
            },
            label: 'Unassign',
            icon: <IconTrash size={14} />,
        },
    ];

    useEffect(() => {
        var tmpCols = [...defaultColumns];
        tmpCols.push(...(CompanyTableColumns[getCompany(aggData)?.code]?.seasonMasters || []));
        // logger.log(' COLUMNS:', tmpCols, getCompany(aggData)?.code);
        setColumns([...tmpCols]);
    }, [aggData]);

    // const columns = CompanyTableColumns[getCompany(aggData)?.code]?.seasonStyles || [];

    const theme = useMantineTheme();

    return (
        <Stack>
            <Text>Select Styles to import to the Season.</Text>

            <Group className='container-action-bar' sx={{ width: '100%' }} position='apart' height={'100%'} py='xs'>
                <CheckboxDropdown selectedItems={selectedItems} onCheckboxClick={onSelectAll} selectMenu={selectAllMenu} />
                <div style={{ display: 'flex', flex: 1, width: '100%' }}>
                    <Group spacing={0} sx={{ flexGrow: 1, display: 'flex', flex: 1 }}>
                        <form onSubmit={handleSearch} style={{ display: 'flex', flex: 1, width: '100%', alignItems: 'center' }}>
                            <Input
                                ref={searchInputRef}
                                sx={{ width: '300px', flexGrow: '0 !important', input: { borderTopRightRadius: '0 !important', borderBottomRightRadius: '0 !important' }, borderRight: 0 }}
                                icon={<IconSearch />}
                                rightSection={keyword.length > 0 && <IconX size={14} onClick={() => setKeyword('')} />}
                                value={keyword}
                                onChange={(e) => setKeyword(e.currentTarget.value)}
                                placeholder='Search for styles...'
                            />
                            <Button type='submit' variant='light' style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }} leftIcon={<IconSearch />}>
                                Search
                            </Button>

                            <Text size='sm' ml={'md'}>
                                Total{' '}
                                <strong>
                                    {filteredData?.length} of {sampleData?.length}
                                </strong>{' '}
                            </Text>
                        </form>
                    </Group>

                    {/* <NativeSelect data={['All Departments', 'Production', 'Post Production']} value={selectedDept} onChange={(event) => setSelectedDept(event.currentTarget.value)} /> */}

                    <Popover shadow='sm' position='bottom-end' opened={filterOpen} onClose={() => setFilterOpen(false)} width={600} withArrow closeOnClickOutside={true}>
                        <Popover.Target>
                            <div>
                                <FilterButton onClick={filterButtonHandler} onClear={onClearClick} count={filterCount} />
                            </div>
                        </Popover.Target>

                        <Popover.Dropdown>
                            <div style={{ display: 'flex' }}>
                                <SeasonMastersStyleFilterPanel
                                    aggData={aggData}
                                    filter={sampleFilter}
                                    onCancel={() => setFilterOpen(false)}
                                    onApply={(vals) => {
                                        setFilterVars(vals);
                                        setFilterOpen(false);
                                    }}
                                    filterItems={filterItems}
                                    columns={columns.filter(
                                        (col) =>
                                            col.accessor &&
                                            col.accessor !== 'expander' &&
                                            col.accessor !== 'action' &&
                                            CompanyTableColumns[company?.code]?.seasonMasters?.some((c) => c.accessor === col.accessor),
                                    )}
                                />
                            </div>
                        </Popover.Dropdown>
                    </Popover>
                </div>
            </Group>

            {/* <ScrollArea style={{ height: 450 }}> */}
            <Box style={{ height: '70vh ', display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={(theme) => ({ flexGrow: 1, overflow: 'auto', marginRight: 0, position: 'relative' })}
                    className={`${sampleFilter.groupBy && sampleFilter.groupBy.length ? 'main-container-content show-data-grouped' : 'main-container-content show-data-grouped-hidden'} ${
                        classes.sampleListContainer
                    }`}>
                    <>
                        <SampleList
                            data={filteredData}
                            onItemClick={onItemClick}
                            onMenuItemClick={onMenuItemClick}
                            onItemSelected={onItemSelected}
                            onStatusClick={onStatusClick}
                            selectedItems={selectedItems}
                            filter={sampleFilter}
                            loading={isLoading}
                            actionMenu={rowMenuItems}
                            paginationSize={200}
                            setFilter={setSampleFilter}
                            columns={columns}
                            allowAssignCapture={false}
                            hiddenColumns={[]}
                            footerStyles={{ left: theme.spacing.md }}
                            aggData={aggData}
                            allowSelect={true}
                        />

                        {filteredData.length === 0 && !isLoading ? (
                            <Box style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                <EmptyState title='No data found' />
                            </Box>
                        ) : null}
                    </>
                </Box>
            </Box>
            <Group position='right' mt='md' style={{ width: '100%', position: 'absolute', bottom: theme.spacing.sm, right: theme.spacing.md, zIndex: 1000 }}>
                <Button loading={isSaving} onClick={() => onSubmitClick()} disabled={selectedItems.length === 0}>
                    Import to Event
                </Button>
            </Group>
        </Stack>
    );
};

const mapStateToProps = (state) => {
    return {
        user: state.auth.user,
        aggData: state.appReducer.aggData,
    };
};

const mapDispatch = {};

export default connect(mapStateToProps, mapDispatch)(MastersStyleImport);

export class MastersStyleFilterModel {
    constructor(obj) {
        this.keyword = '';
        this.apns = [];
        this.departments = [];
        this.colours = [];
        this.variations = [];
        this.variationSizes = [];
        this.productStatus = [];
        this.styleNumbers = [];
        this.pageIndex = 0;
        this.distribution = { dateStart: '', dateEnd: '' };
        this.completed = { dateStart: '', dateEnd: '' };
        this.otbDate = { dateStart: '', dateEnd: '' };
        this.photoCodes = [];

        if (obj) {
            Object.assign(this, obj);
        }
    }
}
